import React, { useState } from 'react';
import { useLoaderData, useNavigate, Form, useActionData, useNavigation, useFetcher } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Project } from '@/types';
import { formatFileSize } from '@/utils/fileUtils';
import { Plus, FolderOpen, LogOut, Trash2 } from 'lucide-react';

const DashboardPage: React.FC = () => {
  const { user, projects } = useLoaderData() as { user: any; projects: Project[] };
  const { logout } = useAuth();
  const navigate = useNavigate();
  const actionData = useActionData() as { error?: string } | undefined;
  const navigation = useNavigation();
  const deleteFetcher = useFetcher();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const isCreating = navigation.state === 'submitting';

  const handleDeleteProject = (project: Project) => {
    if (window.confirm(`Are you sure you want to delete "${project.name}"? This action cannot be undone.`)) {
      deleteFetcher.submit(
        { projectId: project.id.toString() },
        { method: 'post', action: `/projects/${project.id}/delete` }
      );
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-white">Manaflow</h1>
              <span className="ml-2 text-sm text-gray-400">Code Playground</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-300">
                Welcome, {user?.email}
              </span>
              <button
                onClick={logout}
                className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded"
              >
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Projects Header */}
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold text-white">Your Projects</h2>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded text-white font-medium"
          >
            <Plus size={20} />
            <span>New Project</span>
          </button>
        </div>

        {/* Error Message */}
        {actionData?.error && (
          <div className="mb-6 p-4 bg-red-900 border border-red-700 rounded text-red-200">
            {actionData.error}
          </div>
        )}

        {/* Projects Grid */}
        {(
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <FolderOpen size={48} className="mx-auto text-gray-500 mb-4" />
                <h3 className="text-xl font-medium text-gray-400 mb-2">
                  No projects yet
                </h3>
                <p className="text-gray-500 mb-6">
                  Create your first project to get started
                </p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="form-button"
                >
                  Create Project
                </button>
              </div>
            ) : (
              projects.map((project) => (
                <div
                  key={project.id}
                  className="bg-gray-800 border border-gray-700 rounded-lg p-6 hover:border-gray-600 transition-colors"
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-semibold text-white truncate">
                      {project.name}
                    </h3>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleDeleteProject(project)}
                        className="text-gray-400 hover:text-red-400 p-1"
                        title="Delete project"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm text-gray-400 mb-4">
                    <div className="flex justify-between">
                      <span>Size:</span>
                      <span>{formatFileSize(project.size_bytes)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Created:</span>
                      <span>{formatDate(project.created_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Updated:</span>
                      <span>{formatDate(project.updated_at)}</span>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => navigate(`/editor/${project.id}`)}
                      className="flex-1 form-button text-center"
                    >
                      Open Editor
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 className="text-lg font-medium text-white mb-4">
              Create New Project
            </h3>
            
            <Form method="post" onSubmit={() => setShowCreateModal(false)}>
              <div className="mb-4">
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Project Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Enter project name"
                  className="form-input"
                  autoFocus
                  required
                  disabled={isCreating}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Only letters, numbers, underscores, and hyphens are allowed
                </p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="form-button-secondary"
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="form-button"
                >
                  {isCreating ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </Form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;